import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { MiniPill } from '../components/ui';

function CheckGlyph({ size = 24, color = '#10b981' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function InfoColumn({ label, value, sub, action }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{label}</p>
      <p className="text-[13.5px] text-gray-800 mt-1">{value}</p>
      {sub && <p className="text-[12px] text-gray-500 mt-0.5">{sub}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export default function SecurityPage() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState({ enabled: false, verified_at: null });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [showDisable, setShowDisable] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start 2FA setup');
    } finally { setSaving(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const res = await api.post('/auth/2fa/verify', { code: verifyCode });
      setSuccess('Two-factor authentication enabled successfully!');
      if (res.data.recovery_codes) setRecoveryCodes(res.data.recovery_codes);
      setSetupData(null); setVerifyCode('');
      await loadStatus(); await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally { setSaving(false); }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await api.post('/auth/2fa/disable', disableForm);
      setSuccess('Two-factor authentication disabled.');
      setShowDisable(false); setDisableForm({ password: '', code: '' });
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally { setSaving(false); }
  };

  const handleSignOutEverywhere = async () => {
    try {
      await api.post('/auth/logout-everywhere');
      setSuccess('All other sessions have been signed out.');
      setError('');
    } catch (err) {
      setError('Failed to sign out other sessions.');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Security</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[640px]">
          Two-factor authentication and active sessions for your account.
        </p>
      </header>

      {error && (
        <div className="mb-4 bg-rose-50 ring-1 ring-rose-200 text-rose-700 px-4 py-3 rounded-lg text-[13.5px]">{error}</div>
      )}
      {success && (
        <div className="mb-4 bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-[13.5px]">{success}</div>
      )}

      {/* Recovery codes display (after enabling) */}
      {recoveryCodes && (
        <div className="mb-6 bg-amber-50 ring-1 ring-amber-300 rounded-xl p-5">
          <h3 className="font-heading text-[18px] text-amber-800 mb-1">Save your recovery codes</h3>
          <p className="text-[13px] text-amber-700 mb-4">
            These codes can be used to access your account if you lose your authenticator device. Each code is single-use — store them somewhere safe.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {recoveryCodes.map((code, i) => (
              <div key={i} className="font-mono text-[13px] bg-white ring-1 ring-amber-200 rounded px-3 py-2 text-center text-gray-800">{code}</div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => { navigator.clipboard.writeText(recoveryCodes.join('\n')); setSuccess('Recovery codes copied to clipboard'); }}
              className="px-4 py-2 text-[13px] font-medium text-amber-800 bg-amber-100 rounded-md hover:bg-amber-200">Copy to clipboard</button>
            <button onClick={() => setRecoveryCodes(null)}
              className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">I've saved them</button>
          </div>
        </div>
      )}

      {/* 2FA enforcement banner */}
      {user?.requires_2fa_setup && !status.enabled && (
        <div className="mb-6 bg-amber-50 ring-1 ring-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            <div>
              <h3 className="text-[14px] font-semibold text-amber-800">Two-factor authentication required</h3>
              <p className="text-[13px] text-amber-700 mt-1">
                {user?.role === 'admin' || user?.role === 'standard'
                  ? 'As a payroll team member handling sensitive data, you must enable two-factor authentication before continuing.'
                  : 'To protect sensitive employee and payroll data, you must enable two-factor authentication before continuing.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── 2FA card ────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
        {/* Top zone */}
        <div className="px-5 py-4 flex items-start gap-4">
          {status.enabled ? (
            <span className="grid place-items-center shrink-0 w-12 h-12 rounded-lg bg-emerald-100"><CheckGlyph /></span>
          ) : (
            <span className="grid place-items-center shrink-0 w-12 h-12 rounded-lg bg-gray-100 text-gray-500">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-[16px] text-ink dark:text-gray-100">
                {status.enabled ? '2FA is on' : 'Two-factor authentication'}
              </h3>
              {status.enabled ? <MiniPill tone="emerald">Enabled</MiniPill> : <MiniPill tone="gray">Off</MiniPill>}
            </div>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
              {status.enabled
                ? `Enabled${status.verified_at ? ` on ${new Date(status.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} via authenticator app.`
                : 'Add an extra layer of security to your account using an authenticator app such as Google Authenticator, Authy, or 1Password.'}
            </p>
          </div>
          {status.enabled && !showDisable && (
            <button onClick={() => { setShowDisable(true); setError(''); setSuccess(''); }}
              className="text-[13px] font-medium text-rose-600 hover:text-rose-700">Disable</button>
          )}
        </div>

        {/* Bottom zone — info grid when enabled, else the setup body */}
        {status.enabled && !showDisable && (
          <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 px-5 py-4 grid gap-5 sm:grid-cols-3">
            <InfoColumn
              label="Authenticator"
              value="Authenticator app (TOTP)"
              sub={status.verified_at ? `Set up ${new Date(status.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Set up at first login'}
            />
            <InfoColumn
              label="Recovery codes"
              value="Stored at setup"
              sub="A new set was shown when you enabled 2FA. Generate fresh codes by disabling and re-enabling."
            />
            <InfoColumn
              label="Backup method"
              value="Not configured"
              sub="SMS / email backup is on the roadmap."
            />
          </div>
        )}

        {status.enabled && showDisable && (
          <form onSubmit={handleDisable} className="border-t border-gray-100 dark:border-gray-700 px-5 py-4 space-y-4">
            <div className="bg-rose-50 ring-1 ring-rose-200 text-rose-700 px-3 py-2 rounded text-[13px]">
              To disable 2FA, enter your password and a current code from your authenticator app.
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Password</label>
              <input type="password" required value={disableForm.password}
                onChange={(e) => setDisableForm(prev => ({ ...prev, password: e.target.value }))}
                className="w-full rounded-md border-gray-300 text-[13.5px] focus:border-brand-500 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Authentication code</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={disableForm.code}
                onChange={(e) => setDisableForm(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                className="block rounded-md border-gray-300 focus:border-brand-500 focus:ring-brand-500 text-center text-xl tracking-widest font-mono max-w-xs"
                placeholder="000000" autoComplete="one-time-code" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowDisable(false); setDisableForm({ password: '', code: '' }); setError(''); }}
                className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
              <button type="submit" disabled={saving || disableForm.code.length !== 6}
                className="px-4 py-2 text-[13px] font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50">
                {saving ? 'Disabling…' : 'Disable 2FA'}
              </button>
            </div>
          </form>
        )}

        {!status.enabled && !setupData && (
          <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-4">
            <button onClick={handleSetup} disabled={saving}
              className="px-4 py-2 text-[13.5px] font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {saving ? 'Setting up…' : 'Set up 2FA'}
            </button>
          </div>
        )}

        {!status.enabled && setupData && (
          <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-5 space-y-4">
            <div className="bg-sky-50 ring-1 ring-sky-200 text-sky-700 px-3 py-2 rounded text-[13px]">
              <strong>Step 1:</strong> Scan this QR code with your authenticator app.
            </div>
            <div className="flex justify-center py-2">
              <div className="bg-white p-3 rounded-lg ring-1 ring-gray-200">
                <img src={setupData.qr_code} alt="QR Code for 2FA setup" className="w-48 h-48" />
              </div>
            </div>
            <details className="text-[13px]">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Can't scan? Enter this key manually</summary>
              <div className="mt-2 p-3 bg-gray-50 rounded font-mono text-[12px] break-all text-gray-700">{setupData.secret}</div>
            </details>
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="bg-sky-50 ring-1 ring-sky-200 text-sky-700 px-3 py-2 rounded text-[13px]">
                <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app to verify.
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Verification code</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="block rounded-md border-gray-300 focus:border-brand-500 focus:ring-brand-500 text-center text-2xl tracking-widest font-mono max-w-xs"
                  placeholder="000000" autoComplete="one-time-code" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setSetupData(null); setVerifyCode(''); setError(''); }}
                  className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                <button type="submit" disabled={saving || verifyCode.length !== 6}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                  {saving ? 'Verifying…' : 'Verify & enable'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* ── Other devices ───────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 mt-5 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-[16px] text-ink dark:text-gray-100">Other devices</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">If you suspect unauthorised access, sign out of every other device. Your current session stays signed in.</p>
          </div>
          <button onClick={handleSignOutEverywhere}
            className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold bg-rose-50 ring-1 ring-rose-200 text-rose-700 hover:bg-rose-100 whitespace-nowrap">
            Sign out all other devices
          </button>
        </div>
      </div>
    </div>
  );
}
